/**
 * #3917: the lists a generated create sends (`payload.listFields`): a
 * checklist template's items, a survey's questions. Every element is shown
 * with every field it carries, each by its `confirmation.fields.*` label and
 * typed value; an enum value by its `confirmation.enumValues.<field>.<value>`
 * label. Anything this reader cannot show faithfully (a malformed entry, an
 * enum value without a label) makes the payload invalid, and the card then
 * refuses to be confirmed, as every other card section does.
 */
import { Box, Typography } from '@mui/material';
import type { TFunction } from 'i18next';

type Scalar = string | number | boolean | null;

const FIELD_NAME = /^[A-Za-z][A-Za-z0-9]*$/;
const RESERVED = new Set(['constructor', 'prototype', 'toString', 'valueOf', 'hasOwnProperty']);
const TYPES = new Set(['string', 'number', 'integer', 'boolean', 'enum', 'date', 'datetime', 'uuid']);
/** An enum identifier as a bundle key segment (a leading digit allowed: `2_post`). */
export const ENUM_VALUE = /^[A-Za-z0-9][A-Za-z0-9_]*$/;
const MISSING = '\u0000missing';

interface ElementField {
  readonly field: string;
  readonly type: string;
  readonly value: Scalar | readonly ListItem[];
  /** The name of the record an id field points to (a bundle item's part), shown instead of the id. */
  readonly label?: string;
}
type ListItem = { readonly value: Scalar; readonly type: string } | { readonly fields: readonly ElementField[] };
export interface ListField {
  readonly field: string;
  readonly items: readonly ListItem[];
}

export type ListFieldsOf =
  | { readonly kind: 'absent' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'lists'; readonly lists: readonly ListField[] };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const fits = (value: unknown, type: string): value is Scalar => {
  if (value === null) return false;
  if (type === 'boolean') return typeof value === 'boolean';
  if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
  if (type === 'integer') return typeof value === 'number' && Number.isInteger(value);
  return typeof value === 'string';
};

function enumLabel(t: TFunction<'assistant'>, field: string, value: string): string | undefined {
  if (!ENUM_VALUE.test(value) || RESERVED.has(value)) return undefined;
  const label: unknown = t(`confirmation.enumValues.${field}.${value}`, { defaultValue: MISSING, returnObjects: true });
  return typeof label === 'string' && label !== MISSING ? label : undefined;
}

function itemsOf(t: TFunction<'assistant'>, raw: unknown, depth: number, minItems = 1): readonly ListItem[] | undefined {
  if (!Array.isArray(raw) || raw.length < minItems || depth > 2) return undefined;
  const items: ListItem[] = [];
  for (const item of raw) {
    if (!isRecord(item)) return undefined;
    if ('fields' in item) {
      if (!Array.isArray(item['fields']) || item['fields'].length === 0) return undefined;
      const fields: ElementField[] = [];
      const seen = new Set<string>();
      for (const entry of item['fields']) {
        if (!isRecord(entry)) return undefined;
        const { field, type, value } = entry;
        if (typeof field !== 'string' || !FIELD_NAME.test(field) || RESERVED.has(field) || seen.has(field)) return undefined;
        seen.add(field);
        if (type === 'list') {
          // The generated operation supplies its reviewed nested-list minimum.
          // Older payloads without it retain the non-empty requirement.
          const minimum = entry['minItems'] ?? 1;
          if (typeof minimum !== 'number' || !Number.isInteger(minimum) || minimum < 0) return undefined;
          const nested = itemsOf(t, value, depth + 1, minimum);
          if (!nested) return undefined;
          fields.push({ field, type, value: nested });
          continue;
        }
        if (typeof type !== 'string' || !TYPES.has(type) || !fits(value, type)) return undefined;
        if (type === 'enum' && enumLabel(t, field, value as string) === undefined) return undefined;
        const named = entry['label'];
        if (named !== undefined && (type !== 'uuid' || typeof named !== 'string' || named.trim() === '')) return undefined;
        fields.push({ field, type, value, ...(typeof named === 'string' ? { label: named } : {}) });
      }
      items.push({ fields });
      continue;
    }
    const { value, type } = item;
    // Bare enum items carry no field name for resolving their localized label.
    if (type === 'enum' || typeof type !== 'string' || !TYPES.has(type) || !fits(value, type)) return undefined;
    items.push({ value, type });
  }
  return items;
}

/** The payload's lists, or `absent`/`invalid` (fail closed). Scalar-list enums need a field name, so they are refused. */
export function listFieldsOf(t: TFunction<'assistant'>, params: Record<string, unknown>): ListFieldsOf {
  if (!('listFields' in params)) return { kind: 'absent' };
  const raw = params['listFields'];
  if (!Array.isArray(raw) || raw.length === 0) return { kind: 'invalid' };
  const lists: ListField[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    if (!isRecord(entry)) return { kind: 'invalid' };
    const { field } = entry;
    if (typeof field !== 'string' || !FIELD_NAME.test(field) || RESERVED.has(field) || seen.has(field)) return { kind: 'invalid' };
    seen.add(field);
    const items = itemsOf(t, entry['items'], 1);
    if (!items) return { kind: 'invalid' };
    lists.push({ field, items });
  }
  return { kind: 'lists', lists };
}

export function ConfirmationListFields({
  t,
  lists,
  valueText,
}: {
  readonly t: TFunction<'assistant'>;
  readonly lists: readonly ListField[];
  /** The card's own value rendering (booleans, numbers, dates, datetimes), so every section agrees. */
  readonly valueText: (value: Scalar, type: string) => string;
}) {
  const label = (field: string): string => t(`confirmation.fields.${field}`, { defaultValue: field });
  const scalarText = (field: string, value: Scalar, type: string): string =>
    type === 'enum' && typeof value === 'string' ? (enumLabel(t, field, value) ?? value) : valueText(value, type);
  const itemText = (item: ListItem): string =>
    'value' in item
      ? valueText(item.value, item.type)
      : item.fields
          .map((entry) => {
            // A list inside an element: its scalars joined, or each inner object in parentheses.
            const value = Array.isArray(entry.value)
              ? (entry.value as readonly ListItem[]).map((inner) => ('value' in inner ? itemText(inner) : `(${itemText(inner)})`)).join(', ')
              : (entry.label ?? scalarText(entry.field, entry.value as Scalar, entry.type));
            return t('confirmation.recordField', { replace: { field: label(entry.field), value }, defaultValue: `${label(entry.field)}: ${value}` });
          })
          .join(t('confirmation.listFieldSeparator', { defaultValue: '; ' }));
  return (
    <>
      {lists.map((list) => (
        <Box key={`list-${list.field}`} sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('confirmation.listHeader', { replace: { field: label(list.field), count: list.items.length }, defaultValue: label(list.field) })}
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            {list.items.map((item, index) => (
              <Typography component="li" variant="body2" key={`${list.field}-${String(index)}`}>
                {itemText(item)}
              </Typography>
            ))}
          </Box>
        </Box>
      ))}
    </>
  );
}
